/* eslint-disable react/prop-types */
/* eslint-disable no-console */
import { connect } from "react-redux";
import { useState, useEffect, useMemo } from "react";

import Card from "@mui/material/Card";
import Modal from "@mui/material/Modal";
import PropTypes from "prop-types";
import moment from "moment";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import CardTitle from "components/CardTitle";
import { useTranslation } from "react-i18next";
import { useMaterialUIController } from "context";
import DashboardLayout from "components/LayoutContainers/DashboardLayout";
import DashboardNavbar from "components/Navbars/DashboardNavbar";
import Footer from "components/Footer";
import DataTable from "components/DataTable";
import MDAppBar from "components/MDAppBar";
import { updateSupportCase, removeSupportCase } from "store/actions/supportActions";
import { newAlertMessage } from "store/actions/globalAlertActions";
import SupportCaseForm from "./SupportCaseForm";
import supportCaseUtils from "./supportCaseUtils";

const {
  getActiveClientReservationIdsForSupportCases,
  isSupportActive,
  getSupportCasePropertiesByStatus,
} = supportCaseUtils;

const normalizeDateISO = (value) => {
  if (!value) return new Date().toISOString();

  if (typeof value === "string") {
    const d = new Date(value);
    if (d.getTime()) return d.toISOString();
  }

  if (typeof value === "number") return new Date(value).toISOString();

  if (typeof value === "object" && value.value) return new Date(Number(value.value)).toISOString();

  return new Date().toISOString();
};

function CellDate({ value }) {
  return <span>{moment(value).format("DD/MM/YYYY HH:mm")}</span>;
}

function CellStatus({ value }) {
  const { t } = useTranslation();
  const { color, translationKey } = getSupportCasePropertiesByStatus(value);

  return (
    <span
      style={{
        background: color,
        padding: "4px 10px",
        borderRadius: "12px",
        color: "white",
        fontSize: "12px",
        fontWeight: "bold",
        textTransform: "uppercase",
      }}
    >
      {t(translationKey)}
    </span>
  );
}

function CellActive({ value }) {
  return (
    <MDBox display="flex" alignItems="center" justifyContent="center">
      <MDBox
        component="span"
        title={value ? "Active" : "Inactive"}
        sx={{
          width: 12,
          height: 12,
          borderRadius: "50%",
          display: "inline-block",
          backgroundColor: value ? "#2ecc71" : "#7f8c8d",
        }}
      />
    </MDBox>
  );
}

const columns = ({ t }) => [
  { Header: t("name"), accessor: "title", width: 20 },
  { Header: t("project"), accessor: "projectName", maxWidth: 20 },
  { Header: t("apartment"), accessor: "apartmentName", maxWidth: 20 },
  { Header: t("state"), accessor: "status", Cell: CellStatus, maxWidth: 20 },
  { Header: t("createdAt"), accessor: "createdAt", Cell: CellDate, maxWidth: 10 },
];

function AlertDialog({ open, handleClose, handleAgree }) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={handleClose}>
      <MDBox
        sx={({ palette: { white, background } }) => ({
          backgroundColor: darkMode ? background.sidenav : white.main,
        })}
      >
        <DialogTitle>{t("confirmation")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("stepNotUndo")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={handleClose}>{t("cancel")}</MDButton>
          <MDButton onClick={handleAgree} autoFocus>
            {t("OK")}
          </MDButton>
        </DialogActions>
      </MDBox>
    </Dialog>
  );
}

function SupportCases({
  supportCases,
  projects,
  apartments,
  clientReservations,
  mainUserId,
  actUpdateSupportCase,
  actRemoveSupportCase,
  actNewAlertMessage,
}) {
  const [open, setOpen] = useState(false);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [uids, setUids] = useState([]);
  const [actionType, setActionType] = useState("");
  const [supportCasesState, setSupportCasesState] = useState([]);
  const [tabValue, setTabValue] = useState(0);

  const { t } = useTranslation();

  const add = () => {
    setUids([]);
    setActionType("add");
    setOpen(true);
  };

  const edit = (indexes) => {
    const selected = indexes.map((row) => row.original);
    setUids(selected);
    setActionType("edit");
    setOpen(true);
  };

  const remove = (indexes) => {
    const selected = indexes.map((row) => row.original);
    setUids(selected);
    setActionType("remove");
    setOpenConfirm(true);
  };

  const confirm = () => {
    if (actionType === "remove") {
      uids.forEach((supportCase) => {
        const dataToSend = {
          supportCaseId: supportCase?.supportCaseId,
          clientReservationId: supportCase?.clientReservationId,
          apartmentId: supportCase?.apartmentId,
          mainUserId,
          projectId: supportCase?.projectId,
        };

        actRemoveSupportCase(dataToSend, (resp) => {
          const { ok } = resp || {};
          console.log("SupportCases remove resp", { resp, actionType });
          if (!ok) {
            actNewAlertMessage({
              type: "error",
              title: "support",
              content: t("errorRemovingSupportCases"),
            });
            return;
          }

          actNewAlertMessage({
            type: "success",
            title: "support",
            content: t("supportCasesRemovedSuccessfully"),
          });
        });
      });
    }

    setOpenConfirm(false);
  };

  const submit = (data) => {
    const dataToSend = {
      supportCase: data,
      mainUserId,
    };
    actUpdateSupportCase(dataToSend, (resp) => {
      const { ok } = resp || {};
      console.log("SupportCases submit resp", { resp, actionType });
      if (!ok) {
        if (actionType === "edit") {
          actNewAlertMessage({
            type: "error",
            title: "support",
            content: t("errorUpdatingSupportCases"),
          });
          return;
        }

        if (actionType === "add") {
          actNewAlertMessage({
            type: "error",
            title: "support",
            content: t("errorCreatingSupportCases"),
          });
          return;
        }
      }

      if (actionType === "edit") {
        actNewAlertMessage({
          type: "success",
          title: "support",
          content: t("supportCasesUpdatedSuccessfully"),
        });
      }

      if (actionType === "add") {
        actNewAlertMessage({
          type: "success",
          title: "support",
          content: t("supportCasesCreatedSuccessfully"),
        });
      }
      setOpen(false);
    });
  };

  const getInitialValues = () => {
    if (actionType === "edit" && uids.length === 1) {
      return {
        ...uids[0],
        createdAt: normalizeDateISO(uids[0].createdAt),
      };
    }
    return { status: "pending" };
  };

  useEffect(() => {
    if (supportCases) {
      const supportCaseArray = [];

      Object.values(supportCases).forEach((supportCase) => {
        supportCaseArray.push({
          ...supportCase,
          createdAt: normalizeDateISO(supportCase?.createdAt),
          apartmentName: apartments[supportCase?.apartmentId]?.name,
          projectName: projects[supportCase?.projectId]?.name,
        });
      });

      setSupportCasesState(
        supportCaseArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      );
    }
  }, [supportCases, projects, apartments]);

  const activeClientReservationIds = useMemo(
    () => getActiveClientReservationIdsForSupportCases(clientReservations),
    [clientReservations]
  );

  const activeSupportCasesRows = useMemo(
    () =>
      supportCasesState.filter((supportCase) => {
        const isSupportCaseActive = isSupportActive(supportCase, activeClientReservationIds);
        return isSupportCaseActive;
      }),
    [supportCasesState, activeClientReservationIds]
  );

  const allSupportCasesRows = useMemo(
    () =>
      supportCasesState.map((supportCase) => ({
        ...supportCase,
        active: isSupportActive(supportCase, activeClientReservationIds),
      })),
    [supportCasesState, activeClientReservationIds]
  );

  const tableColumns = useMemo(() => {
    if (tabValue === 0) return columns({ t });

    return [
      {
        Header: t("active"),
        accessor: "active",
        Cell: CellActive,
        maxWidth: 10,
      },
      ...columns({ t }),
    ];
  }, [tabValue, t]);

  const rowsToShow = tabValue === 0 ? activeSupportCasesRows : allSupportCasesRows;

  const tabsInfo = [
    { label: t("supportCasesWithActiveClientReservation"), icon: "event_available" },
    { label: t("supportCasesAllExisting"), icon: "event_note" },
  ];

  const titleToShow =
    tabValue === 0 ? t("supportCasesWithActiveClientReservation") : t("supportCasesAllExisting");

  return (
    <DashboardLayout>
      <AlertDialog
        open={openConfirm}
        handleClose={() => setOpenConfirm(false)}
        handleAgree={confirm}
      />

      <Modal open={open} onClose={() => setOpen(false)} style={{ overflow: "scroll" }}>
        <MDBox
          mt={3}
          style={{
            position: "absolute",
            width: "65vw",
            left: "50%",
            transform: "translate(-50%, 0%)",
          }}
        >
          <SupportCaseForm
            onSubmit={submit}
            actionType={actionType}
            uids={uids}
            initialValues={getInitialValues()}
          />
        </MDBox>
      </Modal>

      <DashboardNavbar title={t("support")} route="/support" />

      <MDBox mt={3}>
        <MDAppBar
          tabsInfo={tabsInfo}
          tabValue={tabValue}
          setTabValue={(index) => setTabValue(index)}
        />
      </MDBox>

      <MDBox pt={6} pb={3}>
        <Card>
          <CardTitle title={titleToShow} icon="group" />
          <DataTable
            table={{ columns: tableColumns, rows: rowsToShow }}
            canSearch
            canSelect
            canEdit
            canAdd
            add={add}
            edit={edit}
            remove={remove}
            actionIcon="arrow_forward_ios"
            actionColor="info"
            isSorted
          />
        </Card>
      </MDBox>

      <Footer />
    </DashboardLayout>
  );
}

SupportCases.defaultProps = {
  supportCases: {},
  projects: {},
  apartments: {},
  clientReservations: [],
  mainUserId: "",
};

SupportCases.propTypes = {
  supportCases: PropTypes.object,
  projects: PropTypes.object,
  apartments: PropTypes.object,
  clientReservations: PropTypes.array,
  actUpdateSupportCase: PropTypes.func.isRequired,
  actRemoveSupportCase: PropTypes.func.isRequired,
  mainUserId: PropTypes.string,
  actNewAlertMessage: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
  const {
    apartmentsReducer = {},
    projectsReducer = {},
    clientReservationsReducer = {},
    mainUserReducer = {},
    supportReducer = {},
  } = state;
  const clientReservationsObj = clientReservationsReducer?.clientReservations || {};
  const isProjectManager = mainUserReducer?.isProjectManager;
  const userProjects = mainUserReducer?.projects || {};

  let supportCases = supportReducer;

  let clientReservations = Object.values(clientReservationsObj || {}).flatMap((item) =>
    Object.values(item || {})
  );

  if (isProjectManager) {
    supportCases = Object.fromEntries(
      Object.entries(supportReducer).filter(
        ([, supportCase]) => userProjects?.[supportCase?.projectId]
      )
    );

    clientReservations = clientReservations.filter(
      (reservation) => userProjects?.[reservation?.projectId]
    );
  }

  return {
    mainUserId: mainUserReducer?.uid,
    supportCases,
    projects: projectsReducer,
    apartments: apartmentsReducer,
    clientReservations,
  };
};

const mapDispatchToProps = (dispatch) => ({
  actNewAlertMessage: (message) => {
    dispatch(newAlertMessage(message));
  },
  actUpdateSupportCase: (dataToSend, cb) => dispatch(updateSupportCase({ dataToSend, cb })),
  actRemoveSupportCase: (dataToSend, cb) => dispatch(removeSupportCase({ dataToSend, cb })),
});

export default connect(mapStateToProps, mapDispatchToProps)(SupportCases);
